import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useDrop } from 'react-dnd';
import _isEmpty from 'lodash/isEmpty';
import _some from 'lodash/some';

// Components
import GuestRailSection from './Section/Section';
import NoGuestsSection from './Section/NoGuestsSection';
import Search from './Search/Search';
import Filter from './Filter/Filter';

// Selectors
import { getTablesOfSelectedGuests } from '../../../../selectors/seatingchart/SeatingChartSelector';

// Actions
import {
  fetchChart,
  handleExpandGroup,
  onSeatReorder,
  addPeopleToTable,
  loadGuestList,
  resetFacetValues,
  searchGuestList,
  selectGuest,
  toggleFacetValue,
  toggleFilter,
  unseatSelectedGuests,
  updateGuestListAction,
  updateSeats,
  endDrag,
  startDrag,
} from '../../../../actions/SeatingChartActions';
import { showModal } from '../../../../actions/ModalActions';

import './GuestList.styles.less';

const GuestList = ({
  activeFacetElements,
  groupingsExpanded,
  groupingNames,
  onAddGuestGroup,
  children,
  couple,
  handleGroupingsExpanded,
  currentGroupings,
  searchState,
  searchToken,
  filterState,
  onSearchGuests,
  onSelectGuest,
  onResetFacets,
  onToggleFilter,
  onToggleFacet,
  onStartDrag,
  onEndDrag,
  onUpdateSeats,
  selectedGuests,
  tablesOfSelectedGuests,
  draggingSeatInProgress,
  onUnseatSelectedGuests,
  flowRunId,
  updateFlowRunId,
}) => {
  const [, dropRef] = useDrop({
    accept: 'SEAT',
    drop(item, monitor) {
      const sourceObject = monitor.getItem();
      if (sourceObject.item === 'seat') {
        // if no one is from the sidebar, empty seats of selectedGuests
        if (!_some(tablesOfSelectedGuests, uuid => uuid === null)) {
          onUnseatSelectedGuests();
          onUpdateSeats();
        }
      }
    },
  });

  let noGuests = true;
  const guestGroupsEmpty = currentGroupings == null || _isEmpty(currentGroupings);

  Object.keys(currentGroupings).forEach(affiliation => {
    currentGroupings[affiliation].forEach(guestGroup => {
      if (guestGroup.guests.length > 0) {
        noGuests = false;
      }
    });
  });

  if (!couple.primary || !couple.partner) return null;
  const isCoupleEmpty = !couple.primary.isVisible && !couple.partner.isVisible;

  if (!isCoupleEmpty) {
    noGuests = false;
  }

  return (
    <div className="sc__guest-list" ref={dropRef}>
      <div className="sc__guest-list__header">
        <Search
          searchState={searchState}
          searchToken={searchToken}
          handleOnChange={e => onSearchGuests(e.target.value)}
          handleOnClose={() => onSearchGuests('')}
          noGuests={noGuests}
          onAddGuestGroup={() => onAddGuestGroup(flowRunId, updateFlowRunId)}
        />
        {children}
        <div className="sc__guest-list__header__group">
          <Filter
            activeFacetElements={activeFacetElements}
            className="sc__guest-list__filter-container"
            filterState={filterState}
            onToggleFilter={onToggleFilter}
            onToggleFacet={onToggleFacet}
            noGuests={noGuests}
            onResetFacets={onResetFacets}
          />
          {!filterState && (
            <button
              className="sc__guest-list__add-guest-button"
              onClick={() => onAddGuestGroup(flowRunId, updateFlowRunId)}
            >
              + ADD GUEST
            </button>
          )}
        </div>
      </div>

      {!filterState && (
        <div className="sc__guest-list__body">
          {!isCoupleEmpty && (
            <GuestRailSection
              key="WEDDING_COUPLE"
              affiliation="WEDDING_COUPLE"
              groupingName={groupingNames.WEDDING_COUPLE}
              couple={couple}
              isCouple
              isExpanded={groupingsExpanded.WEDDING_COUPLE}
              searchState={searchState}
              searchToken={searchToken}
              isFiltering={activeFacetElements.length > 0}
              handleGroupingsExpanded={handleGroupingsExpanded}
              onSelectGuest={onSelectGuest}
              onStartDrag={onStartDrag}
              onEndDrag={onEndDrag}
              selectedGuests={selectedGuests}
              draggingSeatInProgress={draggingSeatInProgress}
            />
          )}
          {!guestGroupsEmpty &&
            Object.keys(currentGroupings).map(affiliation => (
              <GuestRailSection
                key={affiliation}
                affiliation={affiliation}
                groupingName={groupingNames[affiliation]}
                groups={currentGroupings[affiliation]}
                isExpanded={groupingsExpanded[affiliation]}
                onSelectGuest={onSelectGuest}
                searchState={searchState}
                searchToken={searchToken}
                isFiltering={activeFacetElements.length > 0}
                handleGroupingsExpanded={handleGroupingsExpanded}
                onStartDrag={onStartDrag}
                onEndDrag={onEndDrag}
                selectedGuests={selectedGuests}
                draggingSeatInProgress={draggingSeatInProgress}
              />
            ))}
          {guestGroupsEmpty && (
            <NoGuestsSection onAddGuestGroup={() => onAddGuestGroup(flowRunId, updateFlowRunId)} />
          )}
        </div>
      )}
    </div>
  );
};

GuestList.propTypes = {
  activeFacetElements: PropTypes.arrayOf(PropTypes.string),
  children: PropTypes.node,
  couple: PropTypes.shape({
    primary: PropTypes.shape({ isVisible: PropTypes.bool }),
    partner: PropTypes.shape({ isVisible: PropTypes.bool }),
  }),
  currentGroupings: PropTypes.shape({
    WEDDING_COUPLE: PropTypes.arrayOf(PropTypes.shape({})),
    PRIMARY_FAMILY: PropTypes.arrayOf(PropTypes.shape({})),
    PARTNER_FAMILY: PropTypes.arrayOf(PropTypes.shape({})),
    PRIMARY_FRIEND: PropTypes.arrayOf(PropTypes.shape({})),
    PARTNER_FRIEND: PropTypes.arrayOf(PropTypes.shape({})),
    PRIMARY_WEDDING_PARTY: PropTypes.arrayOf(PropTypes.shape({})),
    PARTNER_WEDDING_PARTY: PropTypes.arrayOf(PropTypes.shape({})),
    PRIMARY_FAMILY_FRIEND: PropTypes.arrayOf(PropTypes.shape({})),
    PARTNER_FAMILY_FRIEND: PropTypes.arrayOf(PropTypes.shape({})),
    BOTH: PropTypes.arrayOf(PropTypes.shape({})),
    UNKNOWN: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  draggingSeatInProgress: PropTypes.bool,
  filterState: PropTypes.bool,
  handleGroupingsExpanded: PropTypes.func,
  groupingsExpanded: PropTypes.objectOf(PropTypes.bool),
  groupingNames: PropTypes.objectOf(PropTypes.string),
  guestsByGuestDistinction: PropTypes.shape({
    couple: PropTypes.shape({}),
    guest: PropTypes.shape({}),
  }),
  onAddGuestGroup: PropTypes.func,
  onSearchGuests: PropTypes.func,
  onSelectGuest: PropTypes.func,
  onToggleFacet: PropTypes.func,
  onToggleFilter: PropTypes.func,
  onResetFacets: PropTypes.func,
  onStartDrag: PropTypes.func,
  onEndDrag: PropTypes.func,
  onUpdateSeats: PropTypes.func,
  onUnseatSelectedGuests: PropTypes.func,
  searchToken: PropTypes.string,
  searchState: PropTypes.bool,
  selectedGuests: PropTypes.shape({
    couple: PropTypes.array,
    guests: PropTypes.array,
  }),
  tablesOfSelectedGuests: PropTypes.arrayOf(PropTypes.string),
  flowRunId: PropTypes.string,
  updateFlowRunId: PropTypes.func,
};

const mapStateToProps = state => ({
  activeFacetElements: state.guestRail.activeFacetElements,
  couple: state.guestRail.couple,
  currentGroupings: state.guestRail.currentGroupings,
  filterState: state.guestRail.filterState,
  groupingNames: state.guestRail.groupingNames,
  groupingsExpanded: state.guestRail.groupingsExpanded,
  guestsByGuestDistinction: state.guestRail.guestsByGuestDistinction,
  searchState: state.guestRail.searchState,
  searchToken: state.guestRail.searchToken,
  selectedGuests: state.seatingChart.selectedGuests,
  tablesOfSelectedGuests: getTablesOfSelectedGuests(state.seatingChart),
  draggingSeatInProgress: state.seatingChart.draggingSeatInProgress,
});

const mapDispatchToProps = dispatch => ({
  handleGroupingsExpanded: guestGroup => {
    dispatch(handleExpandGroup(guestGroup));
  },
  onSearchGuests: searchToken => {
    dispatch(searchGuestList(searchToken));
    dispatch(loadGuestList());
  },
  onToggleFacet: bucketType => {
    dispatch(toggleFacetValue(bucketType));
    dispatch(loadGuestList());
  },
  onResetFacets: () => {
    dispatch(resetFacetValues());
    dispatch(loadGuestList());
  },
  onToggleFilter: () => dispatch(toggleFilter()),
  onAddGuestGroup: (flowRunId, updateFlowRunId) =>
    dispatch(
      showModal('ADD_GUEST_GROUP', {
        callback: () => dispatch(fetchChart(null)),
        guestGroupType: null,
        sendLinkVisible: false,
        formIdentifier: 'addGuestGroupForm',
        flowRunId,
        updateFlowRunId,
      })
    ),
  onPushSeatsToTable: (tableUUID, guestsByGuestDistinction) => {
    dispatch(addPeopleToTable(tableUUID, guestsByGuestDistinction));
  },
  onSelectGuest: (guest, guestGroup, isCouple) => {
    dispatch(selectGuest(guest, guestGroup, isCouple));
  },
  onReorderSeat: (sourceIndex, hoverIndex, tableUUID, forceReorder) => {
    dispatch(onSeatReorder(sourceIndex, hoverIndex, tableUUID, forceReorder));
  },
  onLoadGuestList: () => dispatch(loadGuestList()),
  onUpdateGuestList: () => dispatch(updateGuestListAction()),
  onUpdateSeats: () => dispatch(updateSeats()),
  onUnseatSelectedGuests: () => dispatch(unseatSelectedGuests()),
  onStartDrag: people => dispatch(startDrag(people)),
  onEndDrag: () => dispatch(endDrag()),
});

export default connect(mapStateToProps, mapDispatchToProps)(GuestList);



// WEBPACK FOOTER //
// ./src/components/manage/seatingchart/GuestList/GuestList.jsx